import {SalesState} from "@portal/api/enums/sales-state";
import {Injectable} from "@angular/core";
import {OrganizationType} from "@portal/api/enums/organization-type";
import {DonationStatus} from "@portal/api/requests/donations/donation-status";
import {PaymentMethod} from "@portal/api/enums/payment-method";
import {UnityType} from "@portal/api/enums/unity-type";
import {TransactionCategory} from "@portal/api/enums/transaction-category";
import {SponsorCategory} from "@portal/api/enums/sponsor-category";
import {StandType} from "@portal/api/enums/stand-type";
import {DocumentType} from "@portal/api/enums/document-type";

@Injectable({
    providedIn: 'root'
})

export class EnumNamesService {
    getSalesStateOptions(): Array<{ value: SalesState, label: string }> {
        return Object.keys(SalesState)
            .filter(key => !isNaN(Number(SalesState[key])))
            .map(key => {
                let label;
                switch (SalesState[key]) {
                    case SalesState.Deal:
                        label = 'Acordado';
                        break;
                    case SalesState.Analyzing:
                        label = 'A analizar';
                        break;
                    case SalesState.Refused:
                        label = 'Recusado';
                        break;
                    case SalesState.ToContact:
                        label = 'A Contactar';
                        break;
                    case SalesState.ContactedViaEmail:
                        label = 'Contactado via email';
                        break;
                    case SalesState.ContactedViaSocialMedia:
                        label = 'Contactado via redes sociais';
                        break;
                    case SalesState.Contacted:
                        label = 'Contactado';
                        break;
                    case SalesState.WaitingMeeting:
                        label = 'A aguardar reunião';
                        break;
                    default:
                        label = 'Estado Desconhecido';
                        break;

                }
                return {value: SalesState[key], label: label};
            }).sort((a, b) => a.label.localeCompare(b.label));
    }

    getOrganizationTypeOptions(): Array<{ value: OrganizationType, label: string }> {
        return Object.keys(OrganizationType)
            .filter(key => !isNaN(Number(OrganizationType[key]))) // exclude computed properties
            .map(key => {
                let label;
                switch (OrganizationType[key]) {
                    case OrganizationType.Club:
                        label = 'Clube';
                        break;
                    case OrganizationType.Tuna:
                        label = 'Tuna';
                        break;
                    case OrganizationType.Association:
                        label = 'Associação';
                        break;
                    case OrganizationType.Party:
                        label = 'Festa popular';
                        break;
                }
                return {value: OrganizationType[key], label: label};
            }).sort((a, b) => a.label.localeCompare(b.label));
    }

    getDonationOptions(): Array<{ value: DonationStatus, label: string }> {
        return Object.keys(DonationStatus)
            .filter(key => !isNaN(Number(DonationStatus[key]))) // exclude computed properties
            .map(key => {
                let label;
                switch (DonationStatus[key]) {
                    case DonationStatus.Paid:
                        label = 'Pago';
                        break;
                    case DonationStatus.ToContact:
                        label = 'A contactar';
                        break;
                    case DonationStatus.Promised:
                        label = 'Prometido';
                        break;
                    case DonationStatus.Denied:
                        label = 'Recusado';
                        break;
                    default:
                        label = key;
                }
                return {value: DonationStatus[key], label: label};
            }).sort((a, b) => a.label.localeCompare(b.label));
    }

    getStandTypesOptions(): Array<{ value: StandType, label: string }> {
        return Object.keys(StandType)
            .filter(key => !isNaN(Number(StandType[key]))) // exclude computed properties
            .map(key => {
                let label;
                switch (StandType[key]) {
                    case StandType.Truck:
                        label = 'Rulote';
                        break;
                    case StandType.Stand:
                        label = 'Barraca';
                        break;
                    case StandType.Tent:
                        label = 'Tenda';
                        break;
                    case StandType.Exhibitors:
                        label = 'Expositor';
                        break;
                    case StandType.Carousels:
                        label = 'Carrossel';
                        break;
                    default:
                        label = key;
                }
                return {value: StandType[key], label: label};
            }).sort((a, b) => a.label.localeCompare(b.label));
    }

    getPaymentMethodsOptions(): Array<{ value: PaymentMethod, label: string }> {
        return Object.keys(PaymentMethod)
            .filter(key => !isNaN(Number(PaymentMethod[key]))) // exclude computed properties
            .filter(key => PaymentMethod[key] !== PaymentMethod.InKind)
            .map(key => {
                let label;
                switch (PaymentMethod[key]) {
                    case PaymentMethod.Cash:
                        label = 'Dinheiro';
                        break;
                    case PaymentMethod.BankTransfer:
                        label = 'Transferência bancária';
                        break;
                    case PaymentMethod.MBWay:
                        label = 'MBWay';
                        break;
                    case PaymentMethod.InKind:
                        label = 'Géneros';
                        break;
                    default:
                        label = key;
                }
                return {value: PaymentMethod[key], label: label};
            }).sort((a, b) => a.label.localeCompare(b.label));
    }

    getSponsorsCategoryOptions(): Array<{ value: SponsorCategory, label: string }> {
        return Object.keys(SponsorCategory)
            .filter(key => !isNaN(Number(SponsorCategory[key]))) // exclude computed properties
            .map(key => {
                let label;
                switch (SponsorCategory[key]) {
                    case SponsorCategory.Construction:
                        label = 'Construção';
                        break;
                    case SponsorCategory.Other:
                        label = 'Outro';
                        break;
                    case SponsorCategory.IndividualCompany:
                        label = 'Empresa individual';
                        break;
                    case SponsorCategory.Restaurant:
                        label = 'Restaurante';
                        break;
                    case SponsorCategory.Trade:
                        label = 'Comércio';
                        break;
                    case SponsorCategory.Workshop:
                        label = 'Oficina';
                        break;
                    default:
                        label = key;
                }
                return {value: SponsorCategory[key], label: label};
            }).sort((a, b) => a.label.localeCompare(b.label));
    }

    getDonationStatusName(value: number): string {
        switch (value) {
            case DonationStatus.Paid:
                return 'Pago';
            case DonationStatus.Promised:
                return 'Prometido';
            case DonationStatus.ToContact:
                return 'A contactar';
            case DonationStatus.Denied:
                return 'Recusado';
        }
    }

    getStandTypeName(value: number): string {
        switch (value) {
            case StandType.Truck:
                return 'Rulote';
            case StandType.Stand:
                return 'Barraca';
            case StandType.Tent:
                return 'Tenda';
            case StandType.Exhibitors:
                return 'Expositor';
            case StandType.Carousels:
                return 'Carrossel';
        }
    }

    getPaymentMethodsName(value: number): string {
        switch (value) {
            case PaymentMethod.Cash:
                return 'Dinheiro';
            case PaymentMethod.BankTransfer:
                return 'Transferência bancária';
            case PaymentMethod.MBWay:
                return 'MBWay';
            case PaymentMethod.InKind:
                return 'Géneros';
        }
    }

    getCategoryName(value: number): string {
        switch (value) {
            case SponsorCategory.Construction:
                return 'Construção';
            case SponsorCategory.Other:
                return 'Outro';
            case SponsorCategory.IndividualCompany:
                return 'Empresa individual';
            case SponsorCategory.Restaurant:
                return 'Restaurante';
            case SponsorCategory.Trade:
                return 'Comércio';
            case SponsorCategory.Workshop:
                return 'Oficina';
        }
    }

    getTransactionCategoryName(value: number): string {
        switch (value) {
            case TransactionCategory.Fixed:
                return 'Fixa';
            case TransactionCategory.General:
                return 'Geral';
            case TransactionCategory.Maintenance:
                return 'Manutenção';
            case TransactionCategory.FeedStock:
                return 'Matéria Prima';
        }
    }

    getUnityTypeOptions(): Array<{ value: UnityType, label: string }> {
        return Object.keys(UnityType)
            .filter(key => !isNaN(Number(UnityType[key]))) // exclude computed properties
            .map(key => {
                let label;
                switch (UnityType[key]) {
                    case UnityType.Unity:
                        label = 'Unidades';
                        break;
                    case UnityType.Liter:
                        label = 'Litros';
                        break;
                    case UnityType.Meter:
                        label = 'Metros';
                        break;
                    case UnityType.Kg:
                        label = 'Kg';
                        break;
                    default:
                        label = key;
                }
                return {value: UnityType[key], label: label};
            }).sort((a, b) => a.label.localeCompare(b.label));
    }

    getTransactionCategoriesOptions(): Array<{ value: TransactionCategory, label: string }> {
        return Object.keys(TransactionCategory)
            .filter(key => !isNaN(Number(TransactionCategory[key]))) // exclude computed properties
            .map(key => {
                let label;
                switch (TransactionCategory[key]) {
                    case TransactionCategory.Fixed:
                        label = 'Fixa';
                        break;
                    case TransactionCategory.General:
                        label = 'Geral';
                        break;
                    case TransactionCategory.FeedStock:
                        label = 'Matéria prima';
                        break;
                    case TransactionCategory.Maintenance:
                        label = 'Manutenção';
                        break;
                    default:
                        label = key;
                }
                return {value: TransactionCategory[key], label: label};
            }).sort((a, b) => a.label.localeCompare(b.label));
    }


    getExpenseDocumentsTypes(): Array<{ value: DocumentType, label: string }> {
        let types = Object.keys(DocumentType)
            .filter(key => !isNaN(Number(DocumentType[key]))) // exclude computed properties
            .map(key => {
                let label;
                switch (DocumentType[key]) {
                    case DocumentType.Other:
                        label = 'Outro';
                        break;
                    case DocumentType.Invoice:
                        label = 'Fatura';
                        break;
                    case DocumentType.Receipt:
                        label = 'Recibo';
                        break;
                    case DocumentType.Contract:
                        label = 'Contrato';
                        break;
                    case DocumentType.License:
                        label = 'Licença';
                        break;
                    case DocumentType.InsurancePolicy:
                        label = 'Apólice de seguro';
                        break;
                    default:
                        label = key;
                }
                return {value: DocumentType[key], label: label};
            }).sort((a, b) => a.label.localeCompare(b.label));

        types = types.filter(x => x.value !== DocumentType.Other);
        types.push({label: 'Outro', value: DocumentType.Other});
        return types;
    }


    getDocumentTypeName(value: number): string {
        switch (value) {
            case DocumentType.Other:
                return 'Outro';
            case DocumentType.Invoice:
                return 'Fatura';
            case DocumentType.Receipt:
                return 'Recibo';
            case DocumentType.Contract:
                return 'Contrato';
            case DocumentType.License:
                return 'Licença';
            case DocumentType.InsurancePolicy:
                return 'Apólice de seguro';
        }
    }
}
